import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { loadModels, getFaceDescriptor, findBestMatch } from '../services/faceService';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CheckCircle, XCircle, Loader2, Fingerprint, Camera, LogIn, LogOut } from 'lucide-react';

const OGP_BLUE = '#0A1628';
const OGP_GOLD = '#C9A84C';

// États de la borne
const STATE = {
  IDLE: 'idle',
  SCANNING: 'scanning',
  SUCCESS: 'success',
  ERROR: 'error',
  CONFIRM: 'confirm',
};

export default function KioskPage() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  const [currentTime, setCurrentTime] = useState(new Date());
  const [state, setState] = useState(STATE.IDLE);
  const [message, setMessage] = useState('Approchez votre visage de la caméra');
  const [subMessage, setSubMessage] = useState('');
  const [matchedUser, setMatchedUser] = useState(null);
  const [allDescriptors, setAllDescriptors] = useState([]);
  const [cameraReady, setCameraReady] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [clockType, setClockType] = useState(null); // 'in' | 'out'
  const [recentActivity, setRecentActivity] = useState([]);

  // Horloge
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Init caméra + modèles
  useEffect(() => {
    initKiosk();
    return () => cleanup();
  }, []);

  const initKiosk = async () => {
    try {
      setMessage('Chargement des modèles IA...');
      await loadModels();
      setModelsLoaded(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 1280, height: 720 }
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraReady(true);

      await loadAllDescriptors();
      setMessage('Approchez votre visage de la caméra');
      startAutoScan();
    } catch (err) {
      setMessage('Erreur caméra — Vérifiez les permissions');
    }
  };

  const cleanup = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
  };

  const loadAllDescriptors = async () => {
    try {
      const { data } = await api.get('/face/all-descriptors');
      setAllDescriptors(data);
    } catch {}
  };

  const startAutoScan = () => {
    if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
    scanIntervalRef.current = setInterval(() => {
      scanFace();
    }, 2000);
  };

  const stopAutoScan = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
  };

  const scanFace = useCallback(async () => {
    if (!videoRef.current || !modelsLoaded || !cameraReady) return;
    if (state !== STATE.IDLE) return;

    try {
      const result = await getFaceDescriptor(videoRef.current);
      if (!result) return;

      const match = findBestMatch(result.descriptor, allDescriptors);
      if (match && match.confidence > 0.6) {
        stopAutoScan();
        setState(STATE.CONFIRM);
        setMatchedUser(match.user);

        // Déterminer si c'est entrée ou sortie
        try {
          const { data } = await api.get(`/attendance/today-for/${match.user.id}`);
          setClockType(data?.clockIn && !data?.clockOut ? 'out' : 'in');
        } catch {
          setClockType('in');
        }
      }
    } catch {}
  }, [modelsLoaded, cameraReady, state, allDescriptors]);

  const handleConfirm = async (type) => {
    setState(STATE.SCANNING);
    setMessage('Enregistrement...');
    try {
      const result = await getFaceDescriptor(videoRef.current);
      const endpoint = type === 'in' ? '/attendance/clock-in-kiosk' : '/attendance/clock-out-kiosk';
      await api.post(endpoint, {
        userId: matchedUser.id,
        faceVerified: true,
        faceConfidence: 0.95,
        method: 'face',
      });

      setState(STATE.SUCCESS);
      setMessage(type === 'in' ? `Bonne journée, ${matchedUser.firstName} !` : `À bientôt, ${matchedUser.firstName} !`);
      setSubMessage(type === 'in' ? '✅ Entrée enregistrée' : '✅ Sortie enregistrée');

      setRecentActivity(prev => [{
        name: `${matchedUser.firstName} ${matchedUser.lastName}`,
        type,
        time: new Date(),
        avatar: matchedUser.avatar,
      }, ...prev.slice(0, 4)]);

      setTimeout(() => {
        setState(STATE.IDLE);
        setMatchedUser(null);
        setMessage('Approchez votre visage de la caméra');
        setSubMessage('');
        startAutoScan();
      }, 3000);
    } catch (err) {
      setState(STATE.ERROR);
      setMessage('Erreur d\'enregistrement');
      setSubMessage(err.response?.data?.error || 'Réessayez');
      setTimeout(() => {
        setState(STATE.IDLE);
        setMatchedUser(null);
        setMessage('Approchez votre visage de la caméra');
        setSubMessage('');
        startAutoScan();
      }, 3000);
    }
  };

  const handleWebAuthn = async () => {
    if (!window.PublicKeyCredential) {
      setMessage('WebAuthn non supporté sur ce navigateur');
      return;
    }
    try {
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: new Uint8Array(32),
          timeout: 60000,
          userVerification: 'required',
        }
      });
      if (credential) {
        setMessage('Touch ID vérifié !');
      }
    } catch (err) {
      setMessage('Touch ID annulé');
    }
  };

  const getBorderColor = () => {
    if (state === STATE.SUCCESS) return '#22c55e';
    if (state === STATE.ERROR) return '#ef4444';
    if (state === STATE.CONFIRM) return OGP_GOLD;
    return OGP_BLUE;
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(135deg,#0A1628 0%,#1A2B4A 55%,#243660 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '52px 24px 24px', fontFamily: "'Raleway',sans-serif",
    }}>
      {/* flag-kiosk : bandeau drapeau + barre Republique */}
      <div style={{position:'fixed',top:0,left:0,right:0,zIndex:50}}>
        <div style={{height:5,display:'flex'}}>
          <div style={{flex:1,background:'#CE1126'}}/><div style={{flex:1,background:'#D4AF37'}}/><div style={{flex:1,background:'#009A44'}}/>
        </div>
        <div style={{background:'rgba(6,12,24,.85)',borderBottom:'1px solid rgba(201,168,76,.25)',padding:'4px 16px',display:'flex',justifyContent:'space-between',flexWrap:'wrap'}}>
          <span style={{color:'#b9c4dc',fontSize:10,letterSpacing:'.18em',textTransform:'uppercase'}}>République de Guinée — Travail · Justice · Solidarité</span>
          <span style={{color:'#C9A84C',fontSize:10,letterSpacing:'.14em',textTransform:'uppercase'}}>Office Guinéen de Publicité</span>
        </div>
      </div>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:32}}>
        <div style={{height:58,display:'flex',alignItems:'center',background:'#fff',borderRadius:12,border:'1px solid rgba(201,168,76,.35)',padding:'7px 14px',boxShadow:'0 4px 14px rgba(0,0,0,.25)'}}>
          <img src="/logo-ogp.png" alt="OGP" style={{height:'100%',width:'auto',objectFit:'contain',display:'block'}} />
        </div>
        <div>
          <h1 style={{color:'#fff',fontSize:24,fontWeight:700,margin:0,letterSpacing:'.5px',fontFamily:"'Cinzel',serif"}}>OGP PRÉSENCE</h1>
          <p style={{color:'#E8CC7A',fontSize:11,margin:'3px 0 0',letterSpacing:'.22em',textTransform:'uppercase'}}>Borne de pointage biométrique</p>
        </div>
        <div style={{marginLeft:'auto',textAlign:'right'}}>
          <p style={{color:'#F9F6EE',fontSize:36,fontWeight:400,margin:0,fontVariantNumeric:'tabular-nums',fontFamily:"'JetBrains Mono',monospace",letterSpacing:'1px'}}>
            {format(currentTime, 'HH:mm:ss')}
          </p>
          <p style={{color:'rgba(255,255,255,0.7)',fontSize:13,margin:0}}>
            {format(currentTime, 'EEEE dd MMMM yyyy', {locale:fr})}
          </p>
        </div>
      </div>

      <div style={{display:'flex',gap:24,width:'100%',maxWidth:1000,alignItems:'flex-start'}}>
        {/* Caméra principale */}
        <div style={{flex:1}}>
          <div style={{
            borderRadius:16, overflow:'hidden', border:'1px solid rgba(248,243,195,.22)',
            boxShadow:'0 8px 30px rgba(0,0,0,.4)', transition:'all 0.3s',
            background:'#0A1628', aspectRatio:'4/3', position:'relative',
          }}>
            <video ref={videoRef} autoPlay muted playsInline
              style={{width:'100%',height:'100%',objectFit:'cover',transform:'scaleX(-1)'}} />

            {/* Overlay selon état */}
            {state === STATE.SUCCESS && (
              <div style={{position:'absolute',inset:0,background:'rgba(34,197,94,0.4)',display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(4px)'}}>
                <CheckCircle style={{width:100,height:100,color:'white'}} />
              </div>
            )}
            {state === STATE.ERROR && (
              <div style={{position:'absolute',inset:0,background:'rgba(239,68,68,0.4)',display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(4px)'}}>
                <XCircle style={{width:100,height:100,color:'white'}} />
              </div>
            )}
            {state === STATE.SCANNING && (
              <div style={{position:'absolute',inset:0,background:'rgba(9,20,34,0.6)',display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(4px)'}}>
                <Loader2 style={{width:80,height:80,color:'white',animation:'spin 1s linear infinite'}} />
              </div>
            )}

            {/* Cadre de visage */}
            {state === STATE.IDLE && (
              <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <div style={{
                  width:200,height:260,border:'2px solid rgba(201,168,76,0.75)',borderRadius:'50%',
                  boxShadow:'0 0 0 4px rgba(201,168,76,0.15)',
                }} />
              </div>
            )}

            {/* Badge utilisateur détecté */}
            {state === STATE.CONFIRM && matchedUser && (
              <div style={{position:'absolute',bottom:16,left:'50%',transform:'translateX(-50%)',
                background:'rgba(9,20,34,0.95)',borderRadius:16,padding:'12px 24px',textAlign:'center',
                border:'1px solid rgba(248,243,195,.22)',backdropFilter:'blur(12px)'}}>
                <p style={{color:OGP_GOLD,fontWeight:700,fontSize:18,margin:0}}>
                  {matchedUser.firstName} {matchedUser.lastName}
                </p>
                <p style={{color:'rgba(255,255,255,0.7)',fontSize:13,margin:'4px 0 0'}}>
                  {matchedUser.role} — {matchedUser.department || 'OGP'}
                </p>
              </div>
            )}
          </div>

          {/* Message état */}
          <div style={{textAlign:'center',marginTop:20}}>
            <p style={{color:'white',fontSize:20,fontWeight:600,margin:0}}>{message}</p>
            {subMessage && <p style={{color:OGP_GOLD,fontSize:15,margin:'8px 0 0'}}>{subMessage}</p>}
          </div>

          {/* Boutons confirmation */}
          {state === STATE.CONFIRM && matchedUser && (
            <div style={{display:'flex',gap:12,marginTop:20}}>
              <button onClick={() => handleConfirm('in')}
                style={{flex:1,background:'linear-gradient(145deg,#4ADE80,#009A44)',color:'#060F1C',border:'none',borderRadius:10,padding:'14px',
                  fontSize:14,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:8,boxShadow:'0 4px 14px rgba(0,154,68,.3)'}}>
                <LogIn style={{width:22,height:22}} /> Pointer Entrée
              </button>
              <button onClick={() => handleConfirm('out')}
                style={{flex:1,background:'linear-gradient(145deg,#FCA5A5,#CE1126)',color:'#060F1C',border:'none',borderRadius:10,padding:'14px',
                  fontSize:14,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:8,boxShadow:'0 4px 14px rgba(206,17,38,.3)'}}>
                <LogOut style={{width:22,height:22}} /> Pointer Sortie
              </button>
              <button onClick={() => { setState(STATE.IDLE); setMatchedUser(null); startAutoScan(); }}
                style={{background:'rgba(255,255,255,.04)',color:'#7A8CA8',border:'1px solid rgba(248,243,195,.12)',borderRadius:10,padding:'14px',
                  fontSize:14,cursor:'pointer'}}>
                ✕
              </button>
            </div>
          )}

          {/* Touch ID */}
          <button onClick={handleWebAuthn}
            style={{marginTop:16,width:'100%',background:'rgba(255,255,255,.04)',color:'#F9F6EE',border:'1px solid rgba(248,243,195,.22)',
              borderRadius:10,padding:'14px',fontSize:13,fontWeight:600,cursor:'pointer',
              display:'flex',alignItems:'center',justifyContent:'center',gap:10,transition:'all .25s'}}>
            <Fingerprint style={{width:22,height:22,color:OGP_GOLD}} />
            Pointer avec Touch ID / Face ID
          </button>
        </div>

        {/* Panel droit - Activité récente */}
        <div style={{width:280,flexShrink:0}}>
          <div style={{background:'rgba(255,255,255,.04)',borderRadius:16,padding:20,border:'1px solid rgba(248,243,195,.12)'}}>
            <h3 style={{color:'#F9F6EE',fontWeight:600,fontSize:13,margin:'0 0 16px',display:'flex',alignItems:'center',gap:8}}>
              <span style={{width:8,height:8,borderRadius:'50%',background:'#22c55e',display:'inline-block'}} />
              Activité récente
            </h3>
            {recentActivity.length === 0 ? (
              <p style={{color:'#4A5D74',fontSize:12,textAlign:'center',padding:'20px 0'}}>
                Aucune activité pour l'instant
              </p>
            ) : recentActivity.map((act, i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 0',borderBottom:'1px solid rgba(248,243,195,.08)'}}>
                <div style={{width:36,height:36,borderRadius:'50%',background:act.type==='in'?'#22c55e':'#ef4444',
                  display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontWeight:700,fontSize:13,flexShrink:0}}>
                  {act.name[0]}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{color:'white',fontSize:13,fontWeight:600,margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {act.name}
                  </p>
                  <p style={{color:act.type==='in'?'#86efac':'#fca5a5',fontSize:11,margin:'2px 0 0'}}>
                    {act.type==='in'?'Entrée':'Sortie'} · {format(act.time,'HH:mm')}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Info */}
          <div style={{background:'rgba(201,168,76,.10)',borderRadius:16,padding:16,marginTop:16,border:'1px solid rgba(201,168,76,.30)'}}>
            <p style={{color:OGP_GOLD,fontSize:13,fontWeight:600,margin:'0 0 8px'}}>ℹ️ Comment pointer</p>
            <p style={{color:'#7A8CA8',fontSize:12,margin:'4px 0'}}>1. Approchez votre visage</p>
            <p style={{color:'#7A8CA8',fontSize:12,margin:'4px 0'}}>2. Attendez la reconnaissance</p>
            <p style={{color:'#7A8CA8',fontSize:12,margin:'4px 0'}}>3. Confirmez Entrée ou Sortie</p>
          </div>
        </div>
      </div>
    </div>
  );
}
